<script setup>
import moment from 'moment'
import { newBookingTimingText } from '@/plugins/misc'

const props = defineProps({
  booking: {
    type: Object,
    required: true
  }
})

// Helper function to get timing text without "in mins/days"
const getBookingTimingText = item => {
  const timingText = newBookingTimingText(item)

  // Remove " in mins", " in hours", " in days" from the end
  return timingText.replace(/\s+in\s+(mins|hours|days)$/i, '')
}

// Calculate time since booking was accepted
const getTimeSinceBooked = item => {
  const acceptedAt = item.acceptedAt
  if (!acceptedAt) return ''

  const now = moment()
  const bookedTime = moment(acceptedAt)
  const diffMinutes = now.diff(bookedTime, 'minutes')

  if (diffMinutes < 60) {
    return `${diffMinutes}m`
  }

  const diffHours = now.diff(bookedTime, 'hours')
  if (diffHours < 24) {
    return `${diffHours}h`
  }

  const diffDays = now.diff(bookedTime, 'days')
  if (diffDays <= 30) {
    return `${diffDays}d`
  }

  const diffMonths = now.diff(bookedTime, 'months')
  if (diffMonths <= 11) {
    return `${diffMonths}mo`
  }

  const diffYears = now.diff(bookedTime, 'years')
  
  return `${diffYears}yr`
}

// Get occasion for booking item
// For instant bookings or reservations on Feb 14, show "Valentine's Day"
const getBookingOccasion = item => {
  if (item.reservationType === 'instant' && moment(item.date, 'YYYY-MM-DD').format('MM-DD') === '02-14') {
    return "Valentine's Day"
  }

  // Check if reservation date is February 14th
  if (item.date && moment(item.date, 'YYYY-MM-DD').format('MM-DD') === '02-14') {
    return "Valentine's Day"
  }
  
  return item.occasion || 'General'
}

// Format the booking date/time
const formatDateTime = item => {
  const time = item.confirmedTime ? item.confirmedTime : item.preferredTime
  
  return moment(item.date + ' ' + time, 'YYYY-MM-DD hh:mm:a')
}
</script>

<template>
  <div class="trending">
    <div
      class="text-center"
      style="margin: auto;"
    >
      <div class="trending-company">
        {{ formatDateTime(booking).format("ddd") }} |
        <VIcon
          icon="mdi-account-multiple-outline"
          size="12"
          style="vertical-align: middle;"
        />
        {{ booking.partySize }} |
        {{ formatDateTime(booking).format("h:mma") }}
      </div>
      <div
        v-if="booking.reservationType === 'general' || booking.reservationType === 'instant'"
        class="trending-company"
      >
        Occasion: {{ getBookingOccasion(booking) }}
      </div>
      <div
        v-if="booking.reservationType === 'general'"
        class="trending-company"
      >
        Secured {{ getBookingTimingText(booking) }}
      </div>
      <div
        v-else-if="booking.reservationType === 'instant'"
        class="trending-company"
      >
        Instant Booking
      </div>
      <div
        v-else
        class="trending-company"
      >
        event booking
      </div>
      <div
        v-if="getTimeSinceBooked(booking)"
        class="trending-company trending-time"
        style="margin-top: 8px;"
      >
        Booked {{ getTimeSinceBooked(booking) }} ago
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.trending {
  padding: 8px 0;
}

.trending-company {
  font-size: 0.8rem;
  line-height: 1.4;
}

.trending-time {
  font-size: 0.56rem; /* 30% smaller than 0.8rem (0.8 * 0.7 = 0.56) */
}
</style>
